import cx from 'classnames';
import * as React from 'react';

import './styles/Coin.scss';

/**
 * ## A `Coin` is a circle with centered content.
 * ### Used most often in conjunction with [Glyph](/internal/components/Glyph)s to render a circle encapsulating a centered icon.
 *
 * ```example
 * <div>
 *   <Coin color="mascot" size="xxxlarge">
 *     <Glyph color="whiteout" name="check" />
 *   </Coin>
 *   <Coin color="apple" size="xlarge">
 *     <Glyph color="whiteout" name="check" />
 *   </Coin>
 *   <Coin border="atlas" size="medium">
 *     <Glyph color="atlas" name="check" />
 *   </Coin>
 *   <Coin border="apple" size="xxxsmall">
 *     <Glyph color="apple" name="check" />
 *   </Coin>
 * </div>
 * ```
 *
 *
 * # TODO
 * * Consider reducing `size`s
 * * Improve centering with flexbox
 * * Consider using `currentColor` to match `border-color` & `color` as default
 */

export type CoinColor =
  | 'apple'
  | 'elmer'
  | 'pencil'
  | 'atlas'
  | 'ballpoint'
  | 'mascot'
  | 'pennant'
  | 'chalkboard'
  | 'graphite'
  | 'percussion'
  | 'paper'
  | 'whiteout'
  | 'facebook'
  | 'pinterest'
  | 'twitter';

export type CoinSize =
  | 'inherit'
  | 'xxxlarge'
  | 'xxlarge'
  | 'xlarge'
  | 'large'
  | 'medium'
  | 'small'
  | 'xsmall'
  | 'xxsmall'
  | 'xxxsmall'
  | 'xxxxsmall';

export type Props = {
  children?: any;
  className?: string;
  color?: CoinColor;
  border?: CoinColor;
  onClick?: () => void;
  // thick border
  thick?: boolean;
  // defines the size of the text/content inside the Coin
  size?: CoinSize;
  // manually sets the width and height of the Coin circle
  width?: number;
  // mutes the background color
  subtle?: boolean;
};

class Coin extends React.Component<Props> {
  render() {
    const { children, className, border, color, size, thick, width, onClick, subtle } = this.props;

    return (
      <span
        onClick={onClick}
        style={{ width, height: width }}
        className={cx(
          'Coin',
          border && `Coin--border-${border}`,
          thick && `Coin--border-${border}-thick`,
          color && `Coin--color-${color}${subtle ? '--subtle' : ''}`,
          size && `Coin--size-${size}`,
          onClick && `Coin--clickable`,
          className
        )}>
        <span className="Coin-content">{children}</span>
      </span>
    );
  }
}

export default Coin;
